import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { User, Facility } from '../types';
import { getProviders, createProvider, updateProvider, deleteProvider } from '../services/api';

interface ProvidersProps {
  user: User;
  onLogout: () => void;
  facilities?: Facility[];
  selectedFacility?: string;
  onFacilityChange?: (facilityId: string) => void;
}

interface Provider {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  provider_type: string;
  specialization: string;
  notification_preference: string;
  patient_count: number;
  primary_assignments: number;
  active: boolean;
}

const Providers: React.FC<ProvidersProps> = ({ user, onLogout, facilities, selectedFacility, onFacilityChange }) => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    provider_type: 'nurse',
    specialization: '',
    notification_preference: 'email'
  });
  const [editingId, setEditingId] = useState<string | null>(null);

  // Debug logging
  useEffect(() => {
    console.log('Providers - showForm state:', showForm);
  }, [showForm]);

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      const data = await getProviders();
      setProviders(data);
    } catch (error) {
      console.error('Error loading providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateProvider(editingId, formData);
      } else {
        await createProvider(formData);
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadProviders();
    } catch (error) {
      console.error('Error saving provider:', error);
      alert('Failed to save provider');
    }
  };

  const handleEdit = (provider: Provider) => {
    setFormData({
      first_name: provider.first_name,
      last_name: provider.last_name,
      email: provider.email,
      phone: provider.phone,
      provider_type: provider.provider_type,
      specialization: provider.specialization || '',
      notification_preference: provider.notification_preference
    });
    setEditingId(provider.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to deactivate this provider?')) return;
    try {
      await deleteProvider(id);
      loadProviders();
    } catch (error) {
      console.error('Error deleting provider:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      provider_type: 'nurse',
      specialization: '',
      notification_preference: 'email'
    });
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} facilities={facilities} selectedFacility={selectedFacility} onFacilityChange={onFacilityChange}>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600">Loading providers...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} facilities={facilities} selectedFacility={selectedFacility} onFacilityChange={onFacilityChange}>
      <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Care Team Providers</h1>
        <button
          onClick={() => {
            console.log('Add Provider button clicked');
            resetForm();
            setEditingId(null);
            setShowForm(true);
            console.log('showForm set to true');
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          + Add Provider
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingId ? 'Edit Provider' : 'New Provider'}
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone (E.164 format)
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+12125551234"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provider Type
              </label>
              <select
                value={formData.provider_type}
                onChange={(e) => setFormData({ ...formData, provider_type: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="nurse">Nurse</option>
                <option value="doctor">Doctor</option>
                <option value="specialist">Specialist</option>
                <option value="caregiver">Caregiver</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Specialization
              </label>
              <input
                type="text"
                value={formData.specialization}
                onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                placeholder="e.g., Cardiology, Geriatrics"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notification Preference
              </label>
              <select
                value={formData.notification_preference}
                onChange={(e) => setFormData({ ...formData, notification_preference: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="email">Email Only</option>
                <option value="sms">SMS Only</option>
                <option value="both">Both Email & SMS</option>
              </select>
            </div>
            <div className="col-span-2 flex justify-end space-x-3 mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingId ? 'Update' : 'Create'} Provider
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Notifications
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Assignments
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {providers.map(provider => (
              <tr key={provider.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">
                    {provider.first_name} {provider.last_name}
                  </div>
                  {provider.specialization && (
                    <div className="text-sm text-gray-500">{provider.specialization}</div>
                  )}
                </td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                    {provider.provider_type}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <div>{provider.email}</div>
                  {provider.phone && <div className="text-gray-500">{provider.phone}</div>}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {provider.notification_preference}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {provider.patient_count} patients
                  {provider.primary_assignments > 0 && (
                    <div className="text-xs text-green-600">
                      {provider.primary_assignments} primary
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-right text-sm space-x-2">
                  <button
                    onClick={() => handleEdit(provider)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(provider.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Deactivate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {providers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No providers found. Click "Add Provider" to create one.
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default Providers;
